import type { Prototype } from './types';

/**
 * The prototype ladder from section 5.1 of the exploration plan.
 *
 * Planned entries are listed from the start so the menu doubles as a visible
 * roadmap during stakeholder demos - you can see what is built and what is not.
 */
export const prototypes: Prototype[] = [
  {
    id: 'p0',
    title: '0 — Toolchain sanity check',
    summary:
      'A tappable 3D object with animation, audio, and live performance stats.',
    proves:
      'Vite + TypeScript + PlayCanvas render, accept pointer and touch input, and play sound on the target device.',
    status: 'ready',
    load: () => import('./p0-sanity'),
  },
  {
    id: 'p1',
    title: '1 — Basic learning game',
    summary:
      'A question or matching activity with large targets, sound, animation, scoring, and reward feedback.',
    proves: 'The browser can deliver satisfying game feel and a fast dev loop.',
    status: 'ready',
    load: () => import('./p1-quiz'),
  },
  {
    id: 'p2',
    title: '2 — Shared IFP game',
    summary:
      'A two- to four-player arithmetic race. Every player gets their own question at the same moment, so correct play means several hands on the glass at once.',
    proves:
      'The shared-screen idea creates something meaningfully different from a tablet app.',
    status: 'ready',
    load: () => import('./p2-race'),
  },
  {
    id: 'p3',
    title: '3 — 3D learning game',
    summary:
      'Drag animals into the right habitat in a stylised 3D scene, with lighting, shadows, drifting camera, particles, and a hand-rolled bounce.',
    proves: 'The practical 3D quality ceiling is high enough for the lessons.',
    status: 'ready',
    load: () => import('./p3-habitat'),
  },
  {
    id: 'p4',
    title: '4 — Stress test',
    summary:
      'Dial object count, animation, physics, shadows, particles and render resolution, then run a benchmark that writes a markdown results table.',
    proves: 'Where performance fails, and which features are expensive.',
    status: 'ready',
    load: () => import('./p4-stress'),
  },
  {
    id: 'p5',
    title: '5 — Licensing a panel',
    summary:
      'Activate a panel with a card code: directly when it has internet, through the teacher\'s phone when it does not. Needs apps/licensing-server running.',
    proves:
      'Per-panel licences can be issued by our server and checked on the panel with no network, and the code a teacher types is short enough to type.',
    status: 'ready',
    load: () => import('./p5-licensing'),
  },
];

export function findPrototype(id: string): Prototype | undefined {
  return prototypes.find((p) => p.id === id);
}
